import React, { useEffect, useMemo, useRef, useState } from "react";
import { turkishIncludes } from "../utils/formatters";
import "./ColumnFilterPopover.css";

/**
 * Tüm ekranlarda / tüm sütunlarda kullanılabilen, DevExpress tarzı
 * sütun başlığı filtre popover'ı.
 * - Kategorik (string/bool): arama + checkbox çoklu seçim + Sıfırla/Tamam
 * - Tarih: operatör (=, sonra, önce, arası) + tarih seçici
 * - Sayısal: operatör (=, ≠, >, ≥, <, ≤, arası) + değer
 */

export type ColumnFilterValue =
  | { kind: "set"; values: string[] }
  | { kind: "cmp"; op: string; v: string; v2?: string };

const NUMERIC = ["int", "float", "decimal", "currency"];

const NUM_OPS = [
  { v: "eq", l: "= (Eşit)" },
  { v: "neq", l: "≠ (Eşit değil)" },
  { v: "gt", l: "> (Büyük)" },
  { v: "gte", l: "≥ (Büyük/eşit)" },
  { v: "lt", l: "< (Küçük)" },
  { v: "lte", l: "≤ (Küçük/eşit)" },
  { v: "between", l: "Arasında" },
];
const DATE_OPS = [
  { v: "eq", l: "= (Eşit)" },
  { v: "gte", l: "Ve sonrası" },
  { v: "gt", l: "Sonra" },
  { v: "lte", l: "Ve öncesi" },
  { v: "lt", l: "Önce" },
  { v: "between", l: "Arasında" },
];

export const isColumnFilterActive = (v?: ColumnFilterValue): boolean =>
  !!v && ((v.kind === "set" && v.values.length > 0) || (v.kind === "cmp" && (v.v ?? "") !== ""));

/** Bir hücrenin (görünen metin + ham değer) filtreye uyup uymadığı. */
export function matchesColumnFilter(
  displayValue: string,
  rawValue: any,
  filter: ColumnFilterValue,
  dataType: string,
): boolean {
  if (filter.kind === "set") {
    if (filter.values.length === 0) return true;
    return filter.values.includes(displayValue);
  }
  const { op, v, v2 } = filter;
  if (v === "" || v == null) return true;

  if (NUMERIC.includes(dataType)) {
    const n = Number(rawValue);
    const a = parseFloat(String(v).replace(",", "."));
    if (isNaN(n) || isNaN(a)) return false;
    const b = v2 ? parseFloat(String(v2).replace(",", ".")) : NaN;
    switch (op) {
      case "eq": return n === a;
      case "neq": return n !== a;
      case "gt": return n > a;
      case "gte": return n >= a;
      case "lt": return n < a;
      case "lte": return n <= a;
      case "between": return isNaN(b) ? n >= a : n >= a && n <= b;
      default: return true;
    }
  }
  if (dataType === "datetime") {
    const d = rawValue ? new Date(rawValue).setHours(0, 0, 0, 0) : NaN;
    const a = v ? new Date(v).setHours(0, 0, 0, 0) : NaN;
    if (isNaN(d) || isNaN(a)) return false;
    const b = v2 ? new Date(v2).setHours(0, 0, 0, 0) : NaN;
    switch (op) {
      case "eq": return d === a;
      case "gt": return d > a;
      case "gte": return d >= a;
      case "lt": return d < a;
      case "lte": return d <= a;
      case "between": return isNaN(b) ? d >= a : d >= a && d <= b;
      default: return true;
    }
  }
  // metin
  const s = displayValue ?? "";
  switch (op) {
    case "eq": return s.toLocaleLowerCase("tr") === String(v).toLocaleLowerCase("tr");
    case "startswith": return s.toLocaleLowerCase("tr").startsWith(String(v).toLocaleLowerCase("tr"));
    default: return turkishIncludes(s, String(v));
  }
}

interface Props {
  dataType: string;
  options?: string[];              // kategorik değerler (checkbox listesi)
  value?: ColumnFilterValue;
  onChange: (v: ColumnFilterValue | undefined) => void;
  align?: "left" | "right";
}

const ColumnFilterPopover: React.FC<Props> = ({ dataType, options = [], value, onChange, align = "left" }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const active = isColumnFilterActive(value);

  const isNumeric = NUMERIC.includes(dataType);
  const isDate = dataType === "datetime";
  const useSet = !isNumeric && !isDate;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onEsc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onEsc); };
  }, [open]);

  return (
    <div className="cfp" ref={ref}>
      <button
        type="button"
        className={`cfp-icon ${active ? "active" : ""}`}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        title="Filtrele"
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
        </svg>
      </button>
      {open && (
        <div className={`cfp-pop cfp-${align}`} onClick={(e) => e.stopPropagation()}>
          {useSet ? (
            <SetFilter
              options={options}
              value={value?.kind === "set" ? value.values : []}
              onApply={(vals) => { onChange(vals.length ? { kind: "set", values: vals } : undefined); setOpen(false); }}
              onReset={() => { onChange(undefined); setOpen(false); }}
            />
          ) : (
            <CmpFilter
              isDate={isDate}
              value={value?.kind === "cmp" ? value : undefined}
              onApply={(f) => { onChange(f.v ? f : undefined); setOpen(false); }}
              onReset={() => { onChange(undefined); setOpen(false); }}
            />
          )}
        </div>
      )}
    </div>
  );
};

const SetFilter: React.FC<{ options: string[]; value: string[]; onApply: (v: string[]) => void; onReset: () => void }> = ({ options, value, onApply, onReset }) => {
  const [search, setSearch] = useState("");
  const [sel, setSel] = useState<string[]>(value);
  const filtered = useMemo(
    () => (search ? options.filter((o) => turkishIncludes(o, search)) : options).slice(0, 500),
    [options, search],
  );
  const toggle = (o: string) => setSel((p) => (p.includes(o) ? p.filter((x) => x !== o) : [...p, o]));
  const allChecked = filtered.length > 0 && filtered.every((o) => sel.includes(o));

  return (
    <div className="cfp-body">
      <div className="cfp-search">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
        <input autoFocus placeholder="Ara…" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      <label className="cfp-check cfp-check-all">
        <input type="checkbox" checked={allChecked}
          onChange={() => setSel(allChecked ? sel.filter((s) => !filtered.includes(s)) : Array.from(new Set([...sel, ...filtered])))} />
        <span>Tümü</span>
      </label>
      <div className="cfp-list">
        {filtered.length === 0 && <div className="cfp-empty">Değer yok</div>}
        {filtered.map((o) => (
          <label key={o} className="cfp-check">
            <input type="checkbox" checked={sel.includes(o)} onChange={() => toggle(o)} />
            <span>{o}</span>
          </label>
        ))}
      </div>
      <div className="cfp-actions">
        <button type="button" className="cfp-reset" onClick={onReset}>Sıfırla</button>
        <button type="button" className="cfp-ok" onClick={() => onApply(sel)}>Tamam</button>
      </div>
    </div>
  );
};

const CmpFilter: React.FC<{ isDate: boolean; value?: { op: string; v: string; v2?: string }; onApply: (f: { kind: "cmp"; op: string; v: string; v2?: string }) => void; onReset: () => void }> = ({ isDate, value, onApply, onReset }) => {
  const ops = isDate ? DATE_OPS : NUM_OPS;
  const [op, setOp] = useState(value?.op || ops[0].v);
  const [v, setV] = useState(value?.v || "");
  const [v2, setV2] = useState(value?.v2 || "");
  const inputType = isDate ? "date" : "number";

  return (
    <div className="cfp-body">
      <select className="cfp-op" value={op} onChange={(e) => setOp(e.target.value)}>
        {ops.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
      <input className="cfp-input" type={inputType} placeholder={isDate ? "Tarih seç" : "Değer"} value={v} onChange={(e) => setV(e.target.value)} autoFocus />
      {op === "between" && (
        <input className="cfp-input" type={inputType} placeholder={isDate ? "Bitiş tarihi" : "Üst değer"} value={v2} onChange={(e) => setV2(e.target.value)} />
      )}
      <div className="cfp-actions">
        <button type="button" className="cfp-reset" onClick={onReset}>Sıfırla</button>
        <button type="button" className="cfp-ok" onClick={() => onApply({ kind: "cmp", op, v, v2: op === "between" ? v2 : undefined })}>Tamam</button>
      </div>
    </div>
  );
};

export default ColumnFilterPopover;
